import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  Target,
  Activity,
  Bell,
  ClipboardCheck,
  Shield,
  AlertTriangle,
  Mail,
  MessageSquare,
  UserCheck,
  Loader2
} from 'lucide-react';

/**
 * PolicyWizard Component
 *
 * Multi-step modal for creating/editing policies:
 * Step 1: Basics - Name and description
 * Step 2: Metric - Condition being monitored
 * Step 3: Actions - What happens when triggered
 * Step 4: Review - Summary and confirmation
 *
 * Note: Scope (environment + app) is selected at activation time, not when creating the policy.
 */

const STEPS = [
  { id: 'basics', label: 'Basics', icon: Target },
  { id: 'metric', label: 'Metric', icon: Activity },
  { id: 'actions', label: 'Actions', icon: Bell },
  { id: 'review', label: 'Review', icon: ClipboardCheck }
];

const COMPARATORS = [
  { value: '>', label: 'Greater than (>)' },
  { value: '>=', label: 'Greater or equal (≥)' },
  { value: '<', label: 'Less than (<)' },
  { value: '<=', label: 'Less or equal (≤)' },
  { value: '==', label: 'Equal (=)' }
];

const TIME_WINDOWS = [
  { value: '5m', label: '5 minutes' },
  { value: '15m', label: '15 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' }
];

// Icon map for action types (backend sends icon name as string)
const ACTION_ICONS = {
  'Shield': Shield,
  'AlertTriangle': AlertTriangle,
  'Mail': Mail,
  'MessageSquare': MessageSquare,
  'UserCheck': UserCheck
};

const PolicyWizard = ({ isOpen, onClose, onSave, existingPolicy = null, metrics = [], actions = [] }) => {
  // Form state
  // Note: Versions are pure templates (metric + threshold + actions).
  // Scope (environment + app) is specified at activation time, not here.
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    metric: {
      type: 'trace_latency_p95',
      comparator: '>',
      threshold: 2000,
      window: '24h'
    },
    actions: []
  });
  const [saving, setSaving] = useState(false);

  // Reset form when opening/closing or loading existing policy
  useEffect(() => {
    if (isOpen) {
      if (existingPolicy) {
        setFormData({
          name: existingPolicy.name || '',
          description: existingPolicy.description || '',
          metric: existingPolicy.metric || { type: 'trace_latency_p95', comparator: '>', threshold: 2000, window: '24h' },
          actions: existingPolicy.actions || []
        });
      } else {
        setFormData({
          name: '',
          description: '',
          metric: { type: 'trace_latency_p95', comparator: '>', threshold: 2000, window: '24h' },
          actions: []
        });
      }
      setCurrentStep(0);
    }
  }, [isOpen, existingPolicy]);

  // Clear incompatible actions when metric type changes
  const metricType = formData.metric.type;
  useEffect(() => {
    const currentMetric = metrics.find(m => m.value === metricType);
    const currentAvailableActions = currentMetric?.available_actions || [];

    setFormData(prev => {
      if (prev.actions.length === 0) return prev;

      const compatibleActions = prev.actions.filter(a =>
        currentAvailableActions.includes(a.type)
      );
      if (compatibleActions.length !== prev.actions.length) {
        return { ...prev, actions: compatibleActions };
      }
      return prev;
    });
  }, [metricType, metrics]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave?.(formData);
      onClose();
    } catch (err) {
      console.error('PolicyWizard: Failed to save policy', err);
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateMetric = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metric: { ...prev.metric, [field]: value }
    }));
  };

  // Get selected metric and its available actions
  const selectedMetric = metrics.find(m => m.value === formData.metric.type);
  const availableActionTypes = selectedMetric?.available_actions || [];

  // Filter actions list to only show available actions for selected metric
  const filteredActions = actions.filter(a => availableActionTypes.includes(a.value));

  const toggleAction = (actionType) => {
    setFormData(prev => {
      const existingAction = prev.actions.find(a => a.type === actionType);
      if (existingAction) {
        return { ...prev, actions: prev.actions.filter(a => a.type !== actionType) };
      } else {
        return { ...prev, actions: [...prev.actions, { type: actionType, config: {} }] };
      }
    });
  };

  const updateActionConfig = (actionType, configKey, configValue) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.map(a =>
        a.type === actionType
          ? { ...a, config: { ...a.config, [configKey]: configValue } }
          : a
      )
    }));
  };

  const isActionEnabled = (actionType) => {
    return formData.actions.some(a => a.type === actionType);
  };

  const getActionConfig = (actionType) => {
    return formData.actions.find(a => a.type === actionType)?.config || {};
  };

  // Validation
  const canProceed = () => {
    switch (currentStep) {
      case 0: // Scope - only name is required (app is optional, environment at activation)
        return formData.name.trim().length > 0;
      case 1: // Metric
        return formData.metric.type && formData.metric.comparator && formData.metric.threshold != null;
      case 2: // Actions
        return formData.actions.length > 0;
      case 3: // Review
        return true;
      default:
        return false;
    }
  };

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center gap-2 px-6 py-3 border-b border-border-subtle bg-background-tertiary">
      {STEPS.map((step, idx) => {
        const StepIcon = step.icon;
        const isActive = idx === currentStep;
        const isCompleted = idx < currentStep;

        return (
          <React.Fragment key={step.id}>
            {idx > 0 && <div className="w-8 h-px bg-border-subtle" />}
            <div className={`flex items-center gap-1.5 ${isActive ? 'text-text-primary' : isCompleted ? 'text-accent-primary-base' : 'text-text-muted'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                isCompleted ? 'bg-accent-primary-base/20 text-accent-primary-base' :
                isActive ? 'bg-accent-primary-base text-background-primary' :
                'bg-surface-overlay text-text-muted'
              }`}>
                {isCompleted ? <Check size={12} /> : <StepIcon size={12} />}
              </div>
              <span className="text-sm">{step.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // Render step content
  const renderScopeStep = () => (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-6">
        <div className="pb-4 mb-2 border-b border-border-subtle">
          <h3 className="text-sm font-semibold text-accent-primary-base uppercase tracking-wider mb-1">Define Policy</h3>
          <p className="text-text-supporting">Name your policy and define its purpose</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
              Policy Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Checkout Latency Guard"
              value={formData.name}
              onChange={(e) => updateFormData('name', e.target.value)}
              className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary-base"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">Description</label>
            <textarea
              placeholder="Brief description of what this policy monitors..."
              value={formData.description}
              onChange={(e) => updateFormData('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary-base resize-none"
            />
          </div>

          <div className="p-3 bg-surface-overlay/30 rounded-lg border border-border-subtle">
            <p className="text-xs text-text-muted">
              Scope (environment and app) is selected when activating the policy.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-border-subtle bg-background-secondary">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed()}
          className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-background-primary rounded text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Next: Metric
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );

  const renderMetricStep = () => (
    <div className="flex flex-col h-full min-h-0">
      {/* Sticky header with back */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border-subtle bg-background-secondary">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} />
          Back
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-6">
        <div className="pb-4 mb-2 border-b border-border-subtle">
          <h3 className="text-sm font-semibold text-accent-primary-base uppercase tracking-wider mb-1">Define Condition</h3>
          <p className="text-text-supporting">Set the metric and threshold that triggers this policy</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
              Metric Type <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.metric.type}
              onChange={(e) => {
                const newMetric = metrics.find(m => m.value === e.target.value);
                // Update type and reset to sensible defaults for this metric
                setFormData(prev => ({
                  ...prev,
                  metric: {
                    ...prev.metric,
                    type: e.target.value,
                    comparator: newMetric?.default_comparator || prev.metric.comparator,
                    threshold: newMetric?.default_threshold ?? prev.metric.threshold,
                    window: newMetric?.has_window === false ? null : (prev.metric.window || '24h')
                  }
                }));
              }}
              className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
            >
              {metrics.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label}{m.is_custom ? ' (Custom)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
                Comparator <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.metric.comparator}
                onChange={(e) => updateMetric('comparator', e.target.value)}
                className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
              >
                {(selectedMetric?.comparators || COMPARATORS.map(c => c.value)).map(comp => {
                  const comparatorInfo = COMPARATORS.find(c => c.value === comp);
                  return (
                    <option key={comp} value={comp}>
                      {comparatorInfo?.label || comp}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
                Threshold <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                step={selectedMetric?.default_threshold < 1 ? '0.1' : '1'}
                placeholder={selectedMetric?.value_hint || 'Enter threshold'}
                value={formData.metric.threshold}
                onChange={(e) => updateMetric('threshold', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary-base"
              />
              {selectedMetric?.value_hint && (
                <p className="text-xs text-text-muted mt-1">{selectedMetric.value_hint}</p>
              )}
            </div>
          </div>

          {/* Time Window - only show for aggregate metrics */}
          {selectedMetric?.has_window !== false && (
            <div>
              <label className="block text-[10px] font-bold text-accent-primary-base uppercase tracking-wide mb-1.5">
                Time Window <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.metric.window || '24h'}
                onChange={(e) => updateMetric('window', e.target.value)}
                className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
              >
                {TIME_WINDOWS.map(w => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Preview */}
          <div className="p-4 bg-surface-overlay/50 rounded-lg border border-border-subtle">
            <span className="text-xs text-text-muted">Condition preview:</span>
            <div className="text-text-primary font-medium mt-1">
              {selectedMetric?.label || formData.metric.type}
              {' '}
              {formData.metric.comparator}
              {' '}
              {formData.metric.threshold}
              {selectedMetric?.unit || ''}
              {selectedMetric?.has_window !== false && (
                <> over {TIME_WINDOWS.find(w => w.value === formData.metric.window)?.label || formData.metric.window}</>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-border-subtle bg-background-secondary">
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed()}
          className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-background-primary rounded text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Next: Actions
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );

  const renderActionsStep = () => (
    <div className="flex flex-col h-full min-h-0">
      {/* Sticky header with back */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border-subtle bg-background-secondary">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} />
          Back
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-4">
        <div className="pb-4 mb-2 border-b border-border-subtle">
          <h3 className="text-sm font-semibold text-accent-primary-base uppercase tracking-wider mb-1">Configure Actions</h3>
          <p className="text-text-supporting">Select one or more actions to execute when the policy is triggered</p>
        </div>

        {filteredActions.length === 0 ? (
          <div className="p-4 bg-surface-overlay/30 rounded-lg border border-border-subtle text-center">
            <p className="text-text-muted">No actions available for the selected metric type.</p>
            <p className="text-xs text-text-muted mt-1">Try selecting a different metric.</p>
          </div>
        ) : (
          filteredActions.map(action => {
            const ActionIcon = ACTION_ICONS[action.icon] || Bell;
            const enabled = isActionEnabled(action.value);
            const config = getActionConfig(action.value);

            return (
              <div
                key={action.value}
                className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                  enabled
                    ? 'border-accent-primary-base bg-accent-primary-base/10'
                    : 'border-border-subtle hover:border-accent-primary-base/50'
                }`}
                onClick={() => toggleAction(action.value)}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                    enabled ? 'bg-accent-primary-base text-background-primary' : 'bg-surface-overlay text-text-muted'
                  }`}>
                    <ActionIcon size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-text-primary">{action.label}</div>
                    <div className="text-sm text-text-muted">{action.description}</div>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    enabled ? 'border-accent-primary-base bg-accent-primary-base' : 'border-border-subtle'
                  }`}>
                    {enabled && <Check size={12} className="text-background-primary" />}
                  </div>
                </div>

                {/* Config input for actions that need it */}
                {enabled && action.has_config && (
                  <div className="mt-3 ml-13" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      placeholder={action.config_label}
                      value={config.value || ''}
                      onChange={(e) => updateActionConfig(action.value, 'value', e.target.value)}
                      className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary-base text-sm"
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-border-subtle bg-background-secondary">
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed()}
          className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-background-primary rounded text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Next: Review
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const selectedMetric = metrics.find(m => m.value === formData.metric.type);
    const selectedWindow = TIME_WINDOWS.find(w => w.value === formData.metric.window);

    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Sticky header with back */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border-subtle bg-background-secondary">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            <ChevronLeft size={16} />
            Back
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-4">
          <div className="pb-4 mb-2 border-b border-border-subtle">
            <h3 className="text-sm font-semibold text-accent-primary-base uppercase tracking-wider mb-1">Review Policy</h3>
            <p className="text-text-supporting">Confirm your policy configuration before creating</p>
          </div>

          {/* Policy Name & Description */}
          <div className="p-4 bg-surface-overlay/50 rounded-lg border border-border-subtle">
            <div className="text-xs text-text-muted mb-1">Policy Name</div>
            <div className="font-semibold text-text-primary">{formData.name || '-'}</div>
            {formData.description && (
              <div className="text-sm text-text-secondary mt-2">{formData.description}</div>
            )}
            <div className="text-xs text-text-muted mt-2">
              Scope (environment + app) selected at activation.
            </div>
          </div>

          {/* Metric Condition */}
          <div className="p-4 bg-surface-overlay/50 rounded-lg border border-border-subtle">
            <div className="text-xs text-text-muted mb-1">Condition</div>
            <div className="font-medium text-text-primary">
              {selectedMetric?.label || formData.metric.type} {formData.metric.comparator} {formData.metric.threshold}{selectedMetric?.unit || ''}
            </div>
            {selectedMetric?.has_window !== false && formData.metric.window && (
              <div className="text-sm text-text-secondary">
                over {selectedWindow?.label || formData.metric.window}
              </div>
            )}
            {selectedMetric?.has_window === false && (
              <div className="text-sm text-text-secondary">
                evaluated per trace
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-4 bg-surface-overlay/50 rounded-lg border border-border-subtle">
            <div className="text-xs text-text-muted mb-2">Actions ({formData.actions.length})</div>
            <div className="flex flex-wrap gap-2">
              {formData.actions.map(action => {
                const actionInfo = actions.find(a => a.value === action.type);
                const ActionIcon = ACTION_ICONS[actionInfo?.icon] || Bell;
                return (
                  <div
                    key={action.type}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-primary-base/20 text-accent-primary-base rounded-lg text-sm"
                  >
                    <ActionIcon size={14} />
                    {actionInfo?.label || action.type}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border-subtle bg-background-secondary">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-background-primary rounded text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {existingPolicy ? 'Save Changes' : 'Create Policy'}
          </button>
        </div>
      </div>
    );
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderScopeStep();
      case 1:
        return renderMetricStep();
      case 2:
        return renderActionsStep();
      case 3:
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-border-subtle rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <h2 className="text-lg font-semibold text-text-primary">
            {existingPolicy ? 'Edit Policy' : 'Create Policy'}
          </h2>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator />

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default PolicyWizard;
